import { Alert, Button, Grid } from "@mui/material";
import { FormikProps, useFormik } from "formik";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import SpinnerArea from "../../../../components/SpinnerArea";
import MainContext from "../../../../context/mainContext";
import useService from "../../../../hooks/useService";
import { FinalDraftModel } from "../../../../models/workflow.model";
import workflowService from "../../../../services/workflow.service";
import WorkflowContext from "../../context/WorkflowContext";
import * as Yup from "yup";
import styles from "./FinalDraft.module.scss";

import SaveSuccessDialog from "../../SaveSuccessDialog";
import CancelDialog from "../../CancelDialog";
import FileUploader, { FileWrapper } from "../../../../components/FileUploader";
import DocumentView from "../../../../components/DocumentView";
import { validateBeforeExecute } from "../../../../misc/utils.functions";

const _emptyDraft: FinalDraftModel = {
  isCompleted: false,
  canEdit: true,
  legalCommentsPending: false,
};

const FinalDraft: React.FC = () => {
  const { ticketNumber } = useParams();
  const { service } = useContext(MainContext);
  const { currentWorkflow, reload } = useContext(WorkflowContext);
  const { t } = useTranslation();
  const [model, loading] = useService(() =>
    workflowService.finalDraft(ticketNumber!)
  );
  const [isReadOnly, setIsReadOnly] = useState(false);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [cancelDialogShow, cancelDialogShowSet] = useState(false);
  const [uploadDocument, setUploadDocument] = useState<File | null>();

  const [requiredDocumentError, setRequiredDocumentError] = useState<
    boolean | null
  >(null);

  const validationSchema = Yup.object().shape({});

  const formik = useFormik<FinalDraftModel>({
    initialValues: model || _emptyDraft,
    onSubmit: () => console.log("submit"),
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const handleSaveCondition = (): boolean => {
    if (!model?.draft && !uploadDocument) {
      setRequiredDocumentError(true);
      return false;
    }

    setRequiredDocumentError(null);
    return true;
  };

  const handleContinueCondition = (): boolean => {
    var saveCondition = handleSaveCondition();

    return saveCondition && !model?.legalCommentsPending;
  };

  const save = (values: FinalDraftModel, nextStep: boolean) => {
    service(
      () =>
        workflowService.finalDraftSave(
          ticketNumber!,
          values,
          nextStep,
          uploadDocument
        ),
      {
        callback: (response) => {
          if (!nextStep) setSaveSuccess(true);
          else {
            reload();
            navigate(
              `/workflow/${response.ticketNumber}/${response.nextStep}`,
              { relative: "path" }
            );
          }
        },
        loader: setSaving,
      }
    );
  };

  const cancel = (formik: FormikProps<FinalDraftModel>) => {
    if (formik.dirty) cancelDialogShowSet(true);
    else navigate(-1);
  };

  useEffect(() => {
    if (model) {
      setIsReadOnly(model.isCompleted || !model.canEdit);
    }
  }, [model]);

  return (
    <SpinnerArea loading={loading || saving}>
      <form
        onSubmit={formik.handleSubmit}
        onReset={formik.handleReset}
        className={styles.finalDraftForm}
      >
        <Grid container>
          <Grid item sm={12}>
            <div className={styles.header}>
              <p>
                <strong>{t("publicationTicketNumber")}: </strong>
                {currentWorkflow?.ticketNumber}
              </p>
              <p className={styles.message}>{t("finalDraftMessage")}</p>
            </div>
          </Grid>
        </Grid>
        <div className={styles.formContainer}>
          <div className={styles["item-1"]}>
            <FileUploader
              onChange={(file: File | File[] | FileWrapper[]) => {
                setUploadDocument(file as File);
              }}
              allowedExtensions={[".doc", ".docx"]}
              isReadOnly={isReadOnly}
              title={t("uploadFinalDraft")!}
              errorValidate={requiredDocumentError}
            />
          </div>
          <div className={styles["item-2"]}>
            {model?.draft && <DocumentView document={model.draft} />}
            {model?.legalCommentsPending && (
              <Alert variant="standard" severity="warning">
                {t("finalDraftLegalTaskPending")}
              </Alert>
            )}
          </div>
        </div>
        <div className={styles.formActions}>
          <Button className={styles.cancel} onClick={() => cancel(formik)}>
            {t("cancel")}
          </Button>
          <Button
            className={styles.save}
            disabled={isReadOnly}
            onClick={() =>
              validateBeforeExecute(
                formik,
                () => save(formik.values, false),
                handleSaveCondition
              )
            }
          >
            {t("save")}
          </Button>
          <Button
            className={styles.continue}
            disabled={isReadOnly}
            onClick={() =>
              validateBeforeExecute(
                formik,
                () => save(formik.values, true),
                handleContinueCondition
              )
            }
          >
            {t("continue")}
          </Button>
        </div>
      </form>
      <SaveSuccessDialog open={saveSuccess} />
      <CancelDialog
        open={cancelDialogShow}
        yesAction={() => navigate(-1)}
        noAction={() => cancelDialogShowSet(false)}
      />
    </SpinnerArea>
  );
};

export default FinalDraft;
